import { type ReactElement } from 'react'

import {
  Grid,
  styled,
  Typography,
  Button,
  Box,
  Input,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
} from '@mui/material'
import classnames from 'classnames'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useAppDispatch } from '@/store'
import { trackEvent, INHERIT_EVENTS } from '@/services/analytics'
// import {
//   setActiveStep,
//   setRuleType,
//   setTimestamp,
//   setPeriodType,
//   setPeriodValue,
//   setStep2Done,
//   RuleType,
// } from '@/store/setUpInheritorsSlice'
import { formatDate } from '@/utils/date'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/en-gb'

import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import useDesktop from '@/hooks/useDesktop'
import { H1, Body2White, Body1White } from '@/components/typography'
import css from './styles.module.css'

import {
  setActiveStep,
  setRuleType,
  setTimestamp,
  setPeriodType,
  setPeriodValue,
  setStep2Done,
  RuleType,
  type InheritanceStep,
} from '@/store/setUpInheritorsV2Slice'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'

dayjs.extend(utc)
dayjs.extend(timezone)

const tomorrow = dayjs().add(1, 'day')

const StyledDatePicker = styled(DatePicker)({
  '>.MuiInputBase-root': {
    color: 'var(--Ebony-Clay-600, #5B6B9C)',
    borderRadius: '4px',
    borderWidth: 0,
    backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
  },
  '>.MuiInputBase-root::hover': {
    backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
  },
})

const notes = [
  'Only you have ownership to this account.',
  'You can totally send or grow your assets any time via well known DeFi protocols such as swap, lending, liquid staking, etc.',
  'You can change these rules anytime.',
]

const SetInheritanceRules = ({
  stepTitle,
  chainIdAddress,
  inheritanceStep,
}: {
  stepTitle?: string
  chainIdAddress: string
  inheritanceStep: InheritanceStep
}): ReactElement => {
  const isDesktop = useDesktop()

  const dispatch = useAppDispatch()

  const rules = inheritanceStep.step2.rules

  const onExpand = (id: number) => (_: any, isExpanded: boolean) => {
    if (isExpanded) dispatch(setRuleType({ chainIdAddress, ruleType: id }))
  }

  const onDatePickerChange = (date: Dayjs) => {
    dispatch(setTimestamp({ chainIdAddress, ruleTimestamp: date.valueOf() }))
  }

  const onSetPeriodType = (type: 'month' | 'year') => {
    dispatch(setPeriodType({ chainIdAddress, rulePeriodType: type }))
  }

  const onPeriodValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? 0 : Number(event.target.value)
    dispatch(setPeriodValue({ chainIdAddress, rulePeriodValue: newValue }))
  }

  const handleNext = () => {
    const ruleTypeLabel = rules.type === RuleType.OnSpecificDate ? 'OnSpecificDate' : 'AfterInactivityPeriod'
    const ruleValueLabel =
      rules.type === RuleType.OnSpecificDate
        ? formatDate(rules.timestamp)
        : `${rules.period.value} ${rules.period.type}`

    trackEvent({ ...INHERIT_EVENTS.ADDED_RULE, ...{ label: `${ruleTypeLabel}_${ruleValueLabel}` } })
    trackEvent({ ...INHERIT_EVENTS.SET_UP_STEP_DONE, ...{ label: 'Step 2' } })
    dispatch(setStep2Done({ chainIdAddress, isDone: true }))
    dispatch(setActiveStep({ chainIdAddress, activeStep: 2 }))
  }

  const handleBack = () => {
    dispatch(setActiveStep({ chainIdAddress, activeStep: 0 }))
  }

  const isDisabledNext = () => {
    return (
      rules.type === RuleType.Unknown ||
      (rules.type === RuleType.OnSpecificDate && !rules.timestamp) ||
      (rules.type === RuleType.AfterInactivityPeriod && rules.period.value === 0)
    )
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Button
              variant="text"
              sx={{
                paddingY: '10px',
                width: '100%',
                borderRadius: '4px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                ':hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              onClick={handleBack}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            <Button
              disabled={isDisabledNext()}
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
              }}
              onClick={handleNext}
            >
              Next
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      container
      sx={
        isDesktop
          ? {
              borderRadius: '4px',
              border: '1px solid var(--Ebony-Clay-950, #2F354B)',
              padding: '16px',
            }
          : {}
      }
    >
      {stepTitle && (
        <Grid item container xs={12}>
          <Grid item container xs={12} flexDirection="column" justifyContent="center">
            <H1>{stepTitle}</H1>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} className={css.addInheritorContent} mt="16px">
        <Box>
          <Typography
            sx={{
              color: 'var(--Ebony-Clay-300, #D2D7E5)',
              fontSize: '16px',
              fontWeight: '400',
              fontStyle: 'normal',
              lineHeight: '160%',
            }}
          >
            When would you like your inheritance to be divided?
          </Typography>
          <Box display="flex" flexDirection="column" flexWrap="wrap" justifyContent="center" alignItems="center" mt={2}>
            <Accordion
              key={1}
              expanded={rules.type === RuleType.OnSpecificDate}
              onChange={onExpand(RuleType.OnSpecificDate)}
            >
              <AccordionSummary
                sx={{ paddingLeft: 0 }}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                  <Radio sx={{ mx: '8px' }} checked={rules.type === RuleType.OnSpecificDate} />
                  <Box py="8px">
                    <Body1White>On a specific date</Body1White>
                    <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                      Set a rule based on an exact date for inheritance distribution.
                    </Body2White>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box px="32px" pb="8px" sx={{ width: '100%' }}>
                  <Body2White mb="4px" sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
                    On this exact date
                  </Body2White>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                    <StyledDatePicker
                      disablePast={true}
                      minDate={tomorrow}
                      slotProps={{
                        textField: {
                          variant: 'filled',
                          inputProps: {
                            sx: { paddingX: '16px', paddingY: '10px', fontSize: '14px' },
                            placeholder: 'DD/MM/YYYY',
                          },
                          InputProps: {
                            disableUnderline: true,
                            sx: { ':hover': { backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)' } },
                          },
                        },
                        openPickerIcon: { sx: { color: 'var(--Ebony-Clay-500, #7C8AB4)' } },
                        popper: { placement: 'bottom-end' },
                      }}
                      sx={{ width: '100%' }}
                      onAccept={(date: unknown) => onDatePickerChange(date as Dayjs)}
                      value={rules.timestamp === 0 ? null : dayjs.tz(rules.timestamp)}
                    />
                  </LocalizationProvider>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion
              key={2}
              expanded={rules.type === RuleType.AfterInactivityPeriod}
              onChange={onExpand(RuleType.AfterInactivityPeriod)}
            >
              <AccordionSummary
                sx={{ paddingLeft: 0 }}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                  <Radio sx={{ mx: '8px' }} checked={rules.type === RuleType.AfterInactivityPeriod} />
                  <Box py="8px">
                    <Body1White>After inactivity period</Body1White>
                    <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                      Set a rule for inheritance after a specified on-chain inactivity.
                    </Body2White>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box px="32px" pb="8px" sx={{ width: '100%' }}>
                  <Body2White mb="4px" sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
                    Inactivity period
                  </Body2White>
                  <Grid
                    container
                    justifyContent="space-between"
                    p="5px"
                    sx={{
                      borderRadius: '4px',
                      backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
                    }}
                  >
                    <Grid item xs>
                      <Input
                        disableUnderline
                        placeholder="0"
                        value={Number(rules.period.value).toString()}
                        sx={{
                          width: '100%',
                          paddingX: '16px',
                          fontSize: '14px',
                          backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
                          borderRadius: '4px',
                          color: 'var(--Ebony-Clay-300, #D2D7E5)',
                        }}
                        inputProps={{
                          type: 'number',
                          min: 0,
                          step: 1,
                        }}
                        onChange={onPeriodValueChange}
                      />
                    </Grid>
                    <Grid item container xs="auto" textAlign="right" alignContent="center">
                      <Button
                        className={classnames(css.monthBtn, rules.period.type !== 'month' && css.btnInactive)}
                        sx={{
                          ':hover': {
                            color: 'var(--Ebony-Clay-300, #D2D7E5)',
                            background: 'var(--Ebony-Clay-800, #3B4569)',
                          },
                        }}
                        variant="text"
                        onClick={() => onSetPeriodType('month')}
                      >
                        Month
                      </Button>
                      <Button
                        className={classnames(css.yearBtn, rules.period.type !== 'year' && css.btnInactive)}
                        variant="text"
                        onClick={() => onSetPeriodType('year')}
                        sx={{
                          ':hover': {
                            color: 'var(--Ebony-Clay-300, #D2D7E5)',
                            background: 'var(--Ebony-Clay-800, #3B4569)',
                          },
                        }}
                      >
                        Year
                      </Button>
                    </Grid>
                  </Grid>
                  {rules.period.value === 0 && (
                    <Body2White
                      sx={{ color: 'var(--Supporting-Carnation, #FF2239)' }}
                    >{`The ${rules.period.type} must be greater than 0`}</Body2White>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box
            mt="16px"
            p="16px"
            sx={{
              borderRadius: '4px',
              border: '1px solid var(--Ebony-Clay-950, #2F354B)',
            }}
          >
            <Body2White sx={{ color: 'var(--Supporting-Koromiko, #FC8313)' }}>
              Please note before the rules are met:
            </Body2White>
            {notes.map((note, index) => {
              return (
                <Grid key={`note-${index}`} container mt="8px">
                  <Grid item container xs="auto" justifyContent="center" alignContent="center">
                    <FiberManualRecordIcon fontSize="small" sx={{ color: '#FC8313', mr: '8px' }} />
                  </Grid>
                  <Grid item xs>
                    <Body2White>{note}</Body2White>
                  </Grid>
                </Grid>
              )
            })}
          </Box>
        </Box>
      </Grid>
      {renderFooter()}
    </Grid>
  )
}

export default SetInheritanceRules
